import { useRef, useMemo, useEffect } from "react";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  tablePlugin,
  linkPlugin,
  linkDialogPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  type MDXEditorMethods,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { Button } from "@/components/ui/button";

const fixMarkdownForMDX = (markdown: string): string => {
  if (!markdown) return "";
  try {
    let normalized = markdown;

    // Fix code block issues
    normalized = normalized.replace(/```text\s*``/g, "```text");
    normalized = normalized.replace(
      /```text\s*\n(.*?)\n```text\s*``/gs,
      "```text\n$1\n```"
    );

    // Escape curly braces that aren't meant to be JSX expressions
    normalized = normalized.replace(/\{(?![^}]*\})/g, "\\{");
    normalized = normalized.replace(/(?<!\{[^{]*)\}(?!\})/g, "\\}");

    // Handle angle brackets that might be interpreted as JSX tags
    normalized = normalized.replace(/<(?![^>]*>)/g, "\\<");
    normalized = normalized.replace(/(?<![<][^<]*)>(?!>)/g, "\\>");

    // Escape < followed immediately by a digit
    normalized = normalized.replace(/<(?=\d)/g, "&lt;");
    // Escape > preceded by a digit
    normalized = normalized.replace(/(?<=\d)>/g, "&gt;");

    // Fix numbered list items that might cause parsing issues
    normalized = normalized.replace(
      /^(\d+)\.\s+(.*)$/gm,
      (_match, num, content) => {
        const sanitizedContent = content.replace(/^([^a-zA-Z_$])/, " $1");
        return `${num}. ${sanitizedContent}`;
      }
    );

    // Handle code blocks
    const lines = normalized.split("\n");
    const result: string[] = [];
    let inCodeBlock = false;

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trimEnd();

      if (line.trim().startsWith("```")) {
        const malformedFence = line.trim().match(/^```(\w+)``+$/);
        if (malformedFence) {
          line = "```" + malformedFence[1];
        }

        if (!inCodeBlock) {
          inCodeBlock = true;
          const afterTicks = line.trim().substring(3).trim();
          if (!afterTicks) {
            result.push("```text");
          } else {
            result.push(line);
          }
        } else {
          inCodeBlock = false;
          result.push("```");
        }
      } else {
        if (!inCodeBlock) {
          line = line.replace(
            /^(\s*)(\d+)(\.\s+)(.*)$/,
            (_match, indent, num, dot, content) => {
              const sanitizedContent = content.replace(
                /^([^a-zA-Z_$\s])/,
                " $1"
              );
              return `${indent}${num}${dot}${sanitizedContent}`;
            }
          );
        }
        result.push(line);
      }
    }

    if (inCodeBlock) {
      result.push("```");
    }

    const fixed = result.join("\n");
    return fixed.replace(/```text\s*``+/g, "```text");
  } catch (error) {
    console.error("Error fixing markdown for MDX:", error);
    return markdown;
  }
};

interface MarkdownEditorProps {
  markdown: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}

export const MarkdownEditor = ({
  markdown,
  onSave,
  onCancel,
}: MarkdownEditorProps) => {
  const editorRef = useRef<MDXEditorMethods>(null);
  const fixedMarkdown = fixMarkdownForMDX(markdown);

  const plugins = useMemo(
    () => [
      headingsPlugin(),
      listsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      markdownShortcutPlugin(),
      tablePlugin(),
      linkPlugin(),
      linkDialogPlugin(),
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <UndoRedo />
            <BoldItalicUnderlineToggles />
            <BlockTypeSelect />
            <CreateLink />
            <InsertTable />
            <InsertThematicBreak />
            <ListsToggle />
          </>
        ),
      }),
    ],
    []
  );

  useEffect(() => {
    setTimeout(() => {
      if (!editorRef.current) return;
      try {
        editorRef.current.setMarkdown(fixedMarkdown);
      } catch {
        try {
          const sanitized = markdown.replace(/[{}]/g, "").replace(/[<>]/g, "");
          editorRef.current.setMarkdown(sanitized);
        } catch {
          editorRef.current.setMarkdown(markdown);
        }
      }
    }, 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    const text = editorRef.current?.getMarkdown() || markdown;
    onSave(text);
  };

  return (
    <div className="overflow-hidden text-sm">
      <div className="border overflow-hidden text-sm [&_.mdxeditor-toolbar]:bg-white">
        <MDXEditor
          key={fixedMarkdown}
          ref={editorRef}
          markdown={fixedMarkdown}
          plugins={plugins}
          overlayContainer={document.body}
        />
      </div>
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30">
        <Button size="sm" variant="brand" onClick={handleSave}>
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
